import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@shared/lib/apiClient';
import { useActiveOrganization } from '@shared/context/ActiveOrganizationContext';
const buildQueryParams = (filters) => {
    const params = {};
    if (filters.categoryId)
        params.categoryId = filters.categoryId;
    if (filters.userId)
        params.userId = filters.userId;
    if (filters.accountId)
        params.accountId = filters.accountId;
    if (filters.period?.start || filters.period?.end) {
        params.range = `${filters.period.start || ''},${filters.period.end || ''}`;
    }
    return params;
};
export const useTransactions = (filters) => {
    const { activeOrganizationId, isLoadingOrganizations } = useActiveOrganization();
    const orgQueryReady = Boolean(activeOrganizationId) && !isLoadingOrganizations;
    return useQuery({
        queryKey: ['transactions', activeOrganizationId, filters],
        enabled: orgQueryReady,
        queryFn: async () => {
            const { data } = await apiClient.get('/transactions', {
                params: buildQueryParams(filters),
            });
            return data;
        },
    });
};
export const useCreateTransaction = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.post('/transactions', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
export const useCreateTransfer = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (payload) => {
            const { data } = await apiClient.post('/transactions/transfer', payload);
            return data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['accounts', activeOrganizationId] });
            }
        },
    });
};
export const useUpdateTransaction = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async ({ id, data }) => {
            const response = await apiClient.patch(`/transactions/${id}`, data);
            return response.data;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
export const useDeleteTransaction = () => {
    const queryClient = useQueryClient();
    const { activeOrganizationId } = useActiveOrganization();
    return useMutation({
        mutationFn: async (id) => {
            await apiClient.delete(`/transactions/${id}`);
            return id;
        },
        onSuccess: () => {
            if (activeOrganizationId) {
                queryClient.invalidateQueries({ queryKey: ['transactions', activeOrganizationId] });
                queryClient.invalidateQueries({ queryKey: ['dashboard', activeOrganizationId] });
            }
        },
    });
};
